import React from 'react';
import { Typography, Card, Row, Col, Statistic, Space, Badge, Divider, Tag, Button, Grid } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { format } from 'date-fns';
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    WarningOutlined,
    DollarOutlined,
    CalendarOutlined,
    BarcodeOutlined,
    GlobalOutlined,
    CreditCardOutlined,
    ShoppingOutlined,
    InfoCircleOutlined,
    IdcardOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { normalizePaymentStatus } from '../constants';
import { getProductTitleById } from '../utils';
import AppTable from '../../Shared/AppTable/AppTable';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const PaymentDetailModal = ({ payment, visible, onClose, products = [] }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();

    // Close large, near-fullscreen modal on mobile with system back
    useMobileFullscreenModalBackHandler(!!visible && screens?.xs, onClose);
    if (!payment) return null;

    const formatDate = (dateString) => {
        return dateString ? format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss') : '';
    };

    const getStatusIcon = (status) => {
        const s = normalizePaymentStatus(status);
        switch (s) {
            case 'paid':
                return <CheckCircleOutlined />;
            case 'disputed':
                return <WarningOutlined />;
            case 'refunded':
                return <ExclamationCircleOutlined />;
            case 'failed':
                return <CloseCircleOutlined />;
            default:
                return null;
        }
    };

    const getStatusColor = (status) => {
        const s = normalizePaymentStatus(status);
        switch (s) {
            case 'paid':
                return '#52c41a';
            case 'disputed':
                return '#fa541c';
            case 'refunded':
                return '#faad14';
            case 'failed':
                return '#f5222d';
            default:
                return '#1890ff';
        }
    };

    const productColumns = [
        {
            title: t('name'),
            dataIndex: 'product_id',
            key: 'product_id',
            render: (_, record) => {
                const key = getProductTitleById(products, record.product_id);
                const translated = key ? t(key) : '';
                const display = translated || key || record.product_id;
                if (typeof display === 'string' && display.length > 0) {
                    return display.charAt(0).toUpperCase() + display.slice(1);
                }
                return display;
            }
        },
        {
            title: t('quantity'),
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
        },
        {
            title: t('unit_amount'),
            dataIndex: 'unit_amount',
            key: 'unit_amount',
            align: 'right',
            render: (text) => `${text} ${payment.currency}`
        },
        {
            title: t('total'),
            key: 'total',
            align: 'right',
            render: (_, record) => `${(record.unit_amount * record.quantity).toFixed(2)} ${payment.currency}`
        }
    ];

    // Calculate payment percentages for the visual breakdown
    const subTotal = payment.sub_total || 0;
    const taxAmount = payment.tax_amount || 0;
    const discountAmount = payment.discount_amount || 0;
    const total = payment.total || 0;

    const taxPercentage = (taxAmount / subTotal * 100).toFixed(1);
    const discountPercentage = (discountAmount / subTotal * 100).toFixed(1);

    return (
        <ResponsiveModal
            title={t('payment_receipt')}
            open={visible}
            onCancel={onClose}
            footer={null}
            desktopWidth={800}
        >
            <div style={{ padding: screens.md ? '24px' : 0 }}>
                {/* Summary Section */}
                <Card bordered={false} className="payment-summary-card" style={{
                    marginBottom: 24,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.09)'
                }}>
                    <Row gutter={[24, 24]} justify="space-between">
                        <Col xs={24} md={10}>
                            <Statistic
                                title={<Text style={{ fontSize: 14 }}>{t('total_paid')}</Text>}
                                value={payment.total}
                                prefix={<DollarOutlined />}
                                suffix={payment.currency}
                                valueStyle={{
                                    color: getStatusColor(payment.payment_status),
                                    fontSize: 32,
                                    fontWeight: 700
                                }}
                            />
                            <Space style={{ marginTop: 6 }}>
                                <Tag color={getStatusColor(payment.payment_status)} icon={getStatusIcon(payment.payment_status)}>
                                    {(() => {
                                        const ns = normalizePaymentStatus(payment.payment_status);
                                        const fb = (ns || 'unknown').replace(/_/g, ' ');
                                        return t(`payment_status.${ns}`, { defaultValue: fb.charAt(0).toUpperCase() + fb.slice(1) });
                                    })()}
                                </Tag>
                                <Tag color="blue">
                                    {(() => {
                                        const pt = payment?.payment_type;
                                        const fallback = (pt || '').toString().replace(/_/g, ' ');
                                        return typeof pt === 'string'
                                            ? t(`payment_type.${pt}`, { defaultValue: fallback })
                                            : fallback;
                                    })()}
                                </Tag>
                            </Space>
                        </Col>
                        <Col xs={0} md={2}>
                            <Divider type="vertical" style={{ height: '100%' }} />
                        </Col>
                        <Col xs={24} md={12}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('payment_date')}</Text>
                                        <Space>
                                            <CalendarOutlined />
                                            <Text>{formatDate(payment.paid_at)}</Text>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col span={12}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('payment_id')}</Text>
                                        <Space>
                                            <IdcardOutlined />
                                            <Text style={{ fontSize: 14 }}>{payment.id}</Text>
                                        </Space>
                                    </Space>
                                </Col>
                                {payment.promotion_code && (
                                    <Col span={24}>
                                        <Space direction="vertical" size={1}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('promotion_applied')}</Text>
                                            <Tag color="gold">{payment.promotion_code}</Tag>
                                        </Space>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Card>

                {/* Payment Breakdown Visual */}
                <Card
                    title={<Space><InfoCircleOutlined /> {t('payment_breakdown')}</Space>}
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="payment-breakdown-card"
                >
                    <div className="payment-breakdown">
                        <Row align="middle" style={{ marginBottom: 12 }}>
                            <Col span={6}>
                                <Text>{t('subtotal')}</Text>
                            </Col>
                            <Col span={12}>

                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong>{payment.sub_total} {payment.currency}</Text>
                            </Col>
                        </Row>

                        <Row align="middle" style={{ marginBottom: 12 }}>
                            <Col span={6}>
                                <Text>{t('tax_percent', { percent: taxPercentage })}</Text>
                            </Col>
                            <Col span={12}>

                            </Col>
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong>{payment.tax_amount} {payment.currency}</Text>
                            </Col>
                        </Row>

                        {payment.discount_amount > 0 && (
                            <Row align="middle" style={{ marginBottom: 12 }}>
                                <Col span={6}>
                                    <Text>{t('discount_percent', { percent: discountPercentage })}</Text>
                                </Col>
                                <Col span={12}>
                                    <div style={{
                                        height: 16,
                                        background: '#ff7875',
                                        borderRadius: 8,
                                        width: `${discountPercentage}%`,
                                        minWidth: 20
                                    }} />
                                </Col>
                                <Col span={6} style={{ textAlign: 'right' }}>
                                    <Text strong>-{payment.discount_amount} {payment.currency}</Text>
                                </Col>
                            </Row>
                        )}

                        <Divider style={{ margin: '16px 0' }} />

                        <Row align="middle">
                            <Col span={6}>
                                <Text strong>{t('total')}</Text>
                            </Col>
                            <Col span={12} />
                            <Col span={6} style={{ textAlign: 'right' }}>
                                <Text strong style={{ color: getStatusColor('succeeded'), fontSize: 18 }}>
                                    {payment.total} {payment.currency}
                                </Text>
                            </Col>
                        </Row>
                    </div>
                </Card>

                {/* Products Table */}
                <Card
                    title={<Space><ShoppingOutlined /> {t('products_purchased')}</Space>}
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="products-card"
                >
                    <AppTable
                        dataSource={payment.products}
                        columns={productColumns}
                        pagination={false}
                        rowKey="product_id"
                        size="small"
                        scroll={{ x: 'max-content' }}
                    />

                    {payment.coins_amount > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <Tag
                                color="gold"
                                style={{
                                    padding: '4px 8px',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    display: 'block',
                                    maxWidth: '100%'
                                }}
                            >
                                <Space size={4} wrap>
                                    <DollarOutlined />
                                    <span style={{ overflowWrap: 'anywhere' }}>
                                        {t('earned_coins_from_transaction', { count: payment.coins_amount })}
                                    </span>
                                </Space>
                            </Tag>
                        </div>
                    )}
                </Card>

                {/* Billing Address */}
                <Card
                    title={<Space><GlobalOutlined /> {t('billing_address')}</Space>}
                    bordered={false}
                    style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                    className="billing-address-card"
                >
                    <Row gutter={[16, 8]}>
                        <Col span={24}>
                            <Text strong>{payment.billing_address.address_line1}</Text>
                        </Col>
                        {payment.billing_address.address_line2 && (
                            <Col span={24}>
                                <Text>{payment.billing_address.address_line2}</Text>
                            </Col>
                        )}
                        <Col span={24}>
                            <Space>
                                <Text>{payment.billing_address.city},</Text>
                                <Text>{payment.billing_address.state}</Text>
                                <Text>{payment.billing_address.postal_code}</Text>
                            </Space>
                        </Col>
                        <Col span={24}>
                            <Text>{payment.billing_address.country}</Text>
                        </Col>
                    </Row>
                </Card>

                {/* Conditional Cards for Special Statuses */}
                {payment.is_refunded && (
                    <Card
                        title={<Space><ExclamationCircleOutlined style={{ color: '#faad14' }} /> {t('refund_information')}</Space>}
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="refund-card"
                        headStyle={{ backgroundColor: '#fffbe6', borderBottom: '1px solid #ffe58f' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('amount_refunded')}</Text>
                                    <Text strong style={{ color: '#faad14', fontSize: 18 }}>
                                        {payment.refund.amount} {payment.currency}
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('refunded_at')}</Text>
                                    <Text>{formatDate(payment.refund.refunded_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('reason')}</Text>
                                    <div style={{
                                        background: '#fffbe6',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        border: '1px solid #ffe58f'
                                    }}>
                                        <Text>{payment.refund.reason}</Text>
                                    </div>
                                </Space>
                            </Col>
                            {payment.refund.failure_reason && (
                                <Col span={24}>
                                    <Space direction="vertical" size={1}>
                                        <Text type="secondary">{t('failure_reason')}</Text>
                                        <div style={{
                                            background: '#fff2e8',
                                            padding: '8px 12px',
                                            borderRadius: 4,
                                            border: '1px solid #ffccc7'
                                        }}>
                                            <Text type="danger">{payment.refund.failure_reason}</Text>
                                        </div>
                                    </Space>
                                </Col>
                            )}
                        </Row>
                    </Card>
                )}

                {payment.is_disputed && (
                    <Card
                        title={<Space><WarningOutlined style={{ color: '#fa541c' }} /> {t('dispute_information')}</Space>}
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="dispute-card"
                        headStyle={{ backgroundColor: '#fff2e8', borderBottom: '1px solid #ffbb96' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('amount_disputed')}</Text>
                                    <Text strong style={{ color: '#fa541c', fontSize: 18 }}>
                                        {payment.dispute.amount} {payment.currency}
                                    </Text>
                                </Space>
                            </Col>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('dispute_status')}</Text>
                                    <Tag color="volcano">{payment.dispute.status}</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} md={8}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('disputed_at')}</Text>
                                    <Text>{formatDate(payment.dispute.disputed_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('reason_for_dispute')}</Text>
                                    <div style={{
                                        background: '#fff2e8',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        border: '1px solid #ffbb96'
                                    }}>
                                        <Text>{payment.dispute.reason}</Text>
                                    </div>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                )}

                {payment.has_error && (
                    <Card
                        title={<Space><CloseCircleOutlined style={{ color: '#f5222d' }} /> {t('payment_failure')}</Space>}
                        bordered={false}
                        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.09)' }}
                        className="failure-card"
                        headStyle={{ backgroundColor: '#fff1f0', borderBottom: '1px solid #ffa39e' }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('failed_at')}</Text>
                                    <Text>{formatDate(payment.failure.failed_at)}</Text>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Space direction="vertical" size={1}>
                                    <Text type="secondary">{t('error_message')}</Text>
                                    <div style={{
                                        background: '#fff1f0',
                                        padding: '8px 12px',
                                        borderRadius: 4,
                                        border: '1px solid #ffa39e'
                                    }}>
                                        <Text type="danger">{payment.failure.message}</Text>
                                    </div>
                                </Space>
                            </Col>
                        </Row>
                    </Card>
                )}
            </div>
        </ResponsiveModal>
    );
};

export default PaymentDetailModal;